/**
 * Generated by orval v7.21.0 🍺
 * Do not edit manually.
 * FastAPI
 * OpenAPI spec version: 0.1.0
 */
import { useMutation, useQuery } from '@tanstack/react-query'
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query'
import { mutationOptionsWithInteraction } from '../../mutationOptions'
import { customInstance } from '../../mutator'
import type {
  HTTPValidationError,
  OrgDefaultSettingsDTO,
  ProjectSettingsDTO,
  ProjectSettingsOverridesDTO,
} from '../fastAPI.schemas'

/**
 * The settings the organisation sets for all of its projects.
 * @summary Get Organisation Default Settings
 */
export const getOrganisationDefaultSettings = (signal?: AbortSignal) => {
  return customInstance<OrgDefaultSettingsDTO>({
    url: `/my-organisation/default-settings`,
    method: 'GET',
    signal,
  })
}

export const getGetOrganisationDefaultSettingsQueryKey = () => {
  return [`/my-organisation/default-settings`] as const
}

export const getGetOrganisationDefaultSettingsQueryOptions = <
  TData = Awaited<ReturnType<typeof getOrganisationDefaultSettings>>,
  TError = HTTPValidationError,
>(options?: {
  query?: UseQueryOptions<Awaited<ReturnType<typeof getOrganisationDefaultSettings>>, TError, TData>
}) => {
  const { query: queryOptions } = options ?? {}

  const queryKey = queryOptions?.queryKey ?? getGetOrganisationDefaultSettingsQueryKey()

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getOrganisationDefaultSettings>>> = ({
    signal,
  }) => getOrganisationDefaultSettings(signal)

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getOrganisationDefaultSettings>>,
    TError,
    TData
  > & { queryKey: QueryKey }
}

export type GetOrganisationDefaultSettingsQueryResult = NonNullable<
  Awaited<ReturnType<typeof getOrganisationDefaultSettings>>
>
export type GetOrganisationDefaultSettingsQueryError = HTTPValidationError

/**
 * @summary Get Organisation Default Settings
 */

export function useGetOrganisationDefaultSettings<
  TData = Awaited<ReturnType<typeof getOrganisationDefaultSettings>>,
  TError = HTTPValidationError,
>(options?: {
  query?: UseQueryOptions<Awaited<ReturnType<typeof getOrganisationDefaultSettings>>, TError, TData>
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetOrganisationDefaultSettingsQueryOptions(options)

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey }

  query.queryKey = queryOptions.queryKey

  return query
}

/**
 * Replace the organisation's default settings (admins only). Projects
that override a setting keep their own value.
 * @summary Update Organisation Default Settings
 */
export const updateOrganisationDefaultSettings = (orgDefaultSettingsDTO: OrgDefaultSettingsDTO) => {
  return customInstance<OrgDefaultSettingsDTO>({
    url: `/my-organisation/default-settings`,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    data: orgDefaultSettingsDTO,
  })
}

export const useUpdateOrganisationDefaultSettingsMutationOptions = <
  TError = HTTPValidationError,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateOrganisationDefaultSettings>>,
    TError,
    { data: OrgDefaultSettingsDTO },
    TContext
  >
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateOrganisationDefaultSettings>>,
  TError,
  { data: OrgDefaultSettingsDTO },
  TContext
> => {
  const mutationKey = ['updateOrganisationDefaultSettings']
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } }

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateOrganisationDefaultSettings>>,
    { data: OrgDefaultSettingsDTO }
  > = props => {
    const { data } = props ?? {}

    return updateOrganisationDefaultSettings(data)
  }

  const customOptions = mutationOptionsWithInteraction(
    { ...mutationOptions, mutationFn },
    { url: `/my-organisation/default-settings` },
    {
      operationId: 'update_organisation_default_settings_my_organisation_default_settings_put',
      operationName: 'updateOrganisationDefaultSettings',
    },
  )

  return customOptions
}

export type UpdateOrganisationDefaultSettingsMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateOrganisationDefaultSettings>>
>
export type UpdateOrganisationDefaultSettingsMutationBody = OrgDefaultSettingsDTO
export type UpdateOrganisationDefaultSettingsMutationError = HTTPValidationError

/**
 * @summary Update Organisation Default Settings
 */
export const useUpdateOrganisationDefaultSettings = <
  TError = HTTPValidationError,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateOrganisationDefaultSettings>>,
    TError,
    { data: OrgDefaultSettingsDTO },
    TContext
  >
}): UseMutationResult<
  Awaited<ReturnType<typeof updateOrganisationDefaultSettings>>,
  TError,
  { data: OrgDefaultSettingsDTO },
  TContext
> => {
  const mutationOptions = useUpdateOrganisationDefaultSettingsMutationOptions(options)

  return useMutation(mutationOptions)
}
/**
 * The project's own settings and its organisation's defaults.
 * @summary Get Project Settings
 */
export const getProjectSettings = (projectGuid: string, signal?: AbortSignal) => {
  return customInstance<ProjectSettingsDTO>({
    url: `/projects/${projectGuid}/project-settings`,
    method: 'GET',
    signal,
  })
}

export const getGetProjectSettingsQueryKey = (projectGuid?: string) => {
  return [`/projects/${projectGuid}/project-settings`] as const
}

export const getGetProjectSettingsQueryOptions = <
  TData = Awaited<ReturnType<typeof getProjectSettings>>,
  TError = HTTPValidationError,
>(
  projectGuid: string,
  options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof getProjectSettings>>, TError, TData>
  },
) => {
  const { query: queryOptions } = options ?? {}

  const queryKey = queryOptions?.queryKey ?? getGetProjectSettingsQueryKey(projectGuid)

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getProjectSettings>>> = ({ signal }) =>
    getProjectSettings(projectGuid, signal)

  return { queryKey, queryFn, enabled: !!projectGuid, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getProjectSettings>>,
    TError,
    TData
  > & { queryKey: QueryKey }
}

export type GetProjectSettingsQueryResult = NonNullable<
  Awaited<ReturnType<typeof getProjectSettings>>
>
export type GetProjectSettingsQueryError = HTTPValidationError

/**
 * @summary Get Project Settings
 */

export function useGetProjectSettings<
  TData = Awaited<ReturnType<typeof getProjectSettings>>,
  TError = HTTPValidationError,
>(
  projectGuid: string,
  options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof getProjectSettings>>, TError, TData>
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetProjectSettingsQueryOptions(projectGuid, options)

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey }

  query.queryKey = queryOptions.queryKey

  return query
}

/**
 * Replace the project's own settings. ``null`` follows the organisation
default.
 * @summary Update Project Settings
 */
export const updateProjectSettings = (
  projectGuid: string,
  projectSettingsOverridesDTO: ProjectSettingsOverridesDTO,
) => {
  return customInstance<ProjectSettingsDTO>({
    url: `/projects/${projectGuid}/project-settings`,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    data: projectSettingsOverridesDTO,
  })
}

export const useUpdateProjectSettingsMutationOptions = <
  TError = HTTPValidationError,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateProjectSettings>>,
    TError,
    { projectGuid: string; data: ProjectSettingsOverridesDTO },
    TContext
  >
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateProjectSettings>>,
  TError,
  { projectGuid: string; data: ProjectSettingsOverridesDTO },
  TContext
> => {
  const mutationKey = ['updateProjectSettings']
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } }

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateProjectSettings>>,
    { projectGuid: string; data: ProjectSettingsOverridesDTO }
  > = props => {
    const { projectGuid, data } = props ?? {}

    return updateProjectSettings(projectGuid, data)
  }

  const customOptions = mutationOptionsWithInteraction(
    { ...mutationOptions, mutationFn },
    { url: `/projects/{projectGuid}/project-settings` },
    {
      operationId: 'update_project_settings_projects__project_guid__project_settings_put',
      operationName: 'updateProjectSettings',
    },
  )

  return customOptions
}

export type UpdateProjectSettingsMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateProjectSettings>>
>
export type UpdateProjectSettingsMutationBody = ProjectSettingsOverridesDTO
export type UpdateProjectSettingsMutationError = HTTPValidationError

/**
 * @summary Update Project Settings
 */
export const useUpdateProjectSettings = <
  TError = HTTPValidationError,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateProjectSettings>>,
    TError,
    { projectGuid: string; data: ProjectSettingsOverridesDTO },
    TContext
  >
}): UseMutationResult<
  Awaited<ReturnType<typeof updateProjectSettings>>,
  TError,
  { projectGuid: string; data: ProjectSettingsOverridesDTO },
  TContext
> => {
  const mutationOptions = useUpdateProjectSettingsMutationOptions(options)

  return useMutation(mutationOptions)
}
